import React, { useState, useEffect } from 'react';
import { 
  Users, UserCheck, Calendar, Clock, FileText, Briefcase, 
  TrendingUp, AlertCircle, CheckCircle, XCircle, UserPlus,
  Building2, IndianRupee, FileSpreadsheet, Mail, Upload, Download,
  Image, CreditCard, Award, Timer, UserMinus, ClipboardList,
  Search, Filter, Eye, Edit, Trash2, Plus, ChevronDown,
  BadgeCheck, IdCard, Phone, MapPin, Shield, UserCog, ScrollText, Presentation
} from 'lucide-react';
import ReportIndexTab from './ReportIndexTab';
import MonthlyAttendanceTab from './MonthlyAttendanceTab';
import LeaveIndexTab from './LeaveIndexTab';
import HRReportsTab from './HRReportsTab';
import SOPTab from '../../components/hr-content/SOPTab';
import CircularTab from '../../components/hr-content/CircularTab';
import PresentationPPTTab from '../../components/hr-content/PresentationPPTTab';
import ProjectWorkStageTab from '../../components/hr-content/ProjectWorkStageTab';
import StaffFormsDetailsTab from '../../components/hr-content/StaffFormsDetailsTab';
import NewJoineeTab from '../../components/hr-content/NewJoineeTab';
import EmployeeManagementTab from '../../components/hr-content/EmployeeManagementTab';
import HROverviewTab from '../../components/hr-content/HROverviewTab';
import EODLocationReportTab from '../../components/hr-content/EODLocationReportTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HRDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [attendanceSubTab, setAttendanceSubTab] = useState('monthly');
  const [attendanceEmployees, setAttendanceEmployees] = useState([]);
  const [showAddEmpDialog, setShowAddEmpDialog] = useState(false);
  const [newEmpName, setNewEmpName] = useState('');
  const [newEmpDept, setNewEmpDept] = useState('');
  const [newEmpDesignation, setNewEmpDesignation] = useState('');
  const [addingEmp, setAddingEmp] = useState(false);
  const [removeMode, setRemoveMode] = useState(false);
  const [selectedForRemoval, setSelectedForRemoval] = useState(new Set());
  const [removingEmps, setRemovingEmps] = useState(false);
  // Edit employee state
  const [editingEmpId, setEditingEmpId] = useState(null);
  const [editEmpName, setEditEmpName] = useState('');
  const [editEmpDept, setEditEmpDept] = useState('');
  const [editEmpDesignation, setEditEmpDesignation] = useState('');
  const [savingEmp, setSavingEmp] = useState(false);

  // Save edited employee — extracted from inline JSX so the async fetch is not
  // dropped during a React re-render of the row's button on `setSavingEmp(true)`.
  const handleSaveEmployee = async (employeeId) => {
    if (!editEmpName.trim()) return;
    setSavingEmp(true);
    try {
      const token = localStorage.getItem('crm_token');
      const response = await fetch(
        `${API}/hr/attendance-employees/${employeeId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            employeeName: editEmpName.trim(),
            department: editEmpDept.trim(),
            designation: editEmpDesignation.trim(),
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.success) {
        setAttendanceEmployees(prev => prev.map(e =>
          e.employeeId === employeeId
            ? { ...e, employeeName: editEmpName.trim().toUpperCase(), department: editEmpDept.trim(), designation: editEmpDesignation.trim() }
            : e
        ));
        setEditingEmpId(null);
        toast.success(`${editEmpName} updated successfully`);
      } else {
        toast.error(data?.detail || `Update failed (${response.status})`);
      }
    } catch (err) {
      toast.error('Failed to update employee');
    } finally {
      setSavingEmp(false);
    }
  };

  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    pendingApprovals: 0,
    onLeave: 0,
    presentToday: 0,
    lateToday: 0,
    absentToday: 0,
    pendingLeaveRequests: 0,
    outsourceVendors: 0,
    outsourceWorkers: 0
  });
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [letters, setLetters] = useState([]);
  const [staffForms, setStaffForms] = useState([]);
  const [staffFormsUploading, setStaffFormsUploading] = useState(false);
  const [staffFormsProgress, setStaffFormsProgress] = useState([]); // [{name, done}]
  const [isDraggingForms, setIsDraggingForms] = useState(false);
  const [outsourceVendors, setOutsourceVendors] = useState([]);
  const [outsourceWorkers, setOutsourceWorkers] = useState([]);
  const [departmentDistribution, setDepartmentDistribution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDept, setFilterDept] = useState('all');

  // Dialog states
  const [showAddEmployeeDialog, setShowAddEmployeeDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showLetterDialog, setShowLetterDialog] = useState(false);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showWorkerDialog, setShowWorkerDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedLetterType, setSelectedLetterType] = useState('');

  useEffect(() => {
    fetchHRData();
  }, []);

  const fetchHRData = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch dashboard data
      const dashboardRes = await axios.get(`${API}/hr/dashboard-data`, { headers }).catch(() => ({ data: {} }));
      const dashboardData = dashboardRes.data || {};

      if (dashboardData.stats) {
        setStats(dashboardData.stats);
      }
      if (dashboardData.departmentDistribution) {
        setDepartmentDistribution(dashboardData.departmentDistribution);
      }
      if (dashboardData.recentLeaveRequests) {
        setLeaveRequests(dashboardData.recentLeaveRequests);
      }

      // Fetch employees (Excel-based attendance employees)
      const attEmpRes = await axios.get(`${API}/hr/attendance-employees`, { headers }).catch(() => ({ data: { employees: [] } }));
      setAttendanceEmployees(attEmpRes.data.employees || []);

      // Fetch CRM users for legacy reference (used in letters/payroll/etc.)
      const usersRes = await axios.get(`${API}/crm/admin/users`, { headers }).catch(() => ({ data: [] }));
      setEmployees(usersRes.data || []);

      // Fetch attendance
      const attendanceRes = await axios.get(`${API}/hr/attendance`, { headers }).catch(() => ({ data: { attendance: [] } }));
      setAttendance(attendanceRes.data.attendance || []);

      // Fetch all leave requests
      const leavesRes = await axios.get(`${API}/hr/leaves`, { headers }).catch(() => ({ data: { leaves: [] } }));
      setLeaveRequests(leavesRes.data.leaves || []);

      // Fetch payslips
      const payslipsRes = await axios.get(`${API}/hr/payslips`, { headers }).catch(() => ({ data: { payslips: [] } }));
      setPayslips(payslipsRes.data.payslips || []);

      // Fetch letters
      const lettersRes = await axios.get(`${API}/hr/letters`, { headers }).catch(() => ({ data: { letters: [] } }));
      setLetters(lettersRes.data.letters || []);

      // Fetch staff form templates
      const formsRes = await axios.get(`${API}/hr/staff-forms`, { headers }).catch(() => ({ data: { forms: [] } }));
      setStaffForms(formsRes.data.forms || []);

      // Fetch outsource vendors
      const vendorsRes = await axios.get(`${API}/hr/outsource/vendors`, { headers }).catch(() => ({ data: { vendors: [] } }));
      setOutsourceVendors(vendorsRes.data.vendors || []);

      // Fetch outsource workers
      const workersRes = await axios.get(`${API}/hr/outsource/workers`, { headers }).catch(() => ({ data: { workers: [] } }));
      setOutsourceWorkers(workersRes.data.workers || []);

    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      await axios.post(`${API}/hr/attendance/sign-in`, null, {
        params: { employee_id: user?.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Signed in successfully!');
      fetchHRData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign in');
    }
  };

  const handleSignOut = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      await axios.post(`${API}/hr/attendance/sign-out`, null, {
        params: { employee_id: user?.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Signed out successfully!');
      fetchHRData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign out');
    }
  };

  const handleLeaveAction = async (leaveId, status) => {
    try {
      const token = localStorage.getItem('crm_token');
      await axios.patch(`${API}/hr/leaves/${leaveId}`, null, {
        params: { status, approver_id: user?.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Leave ${status}!`);
      fetchHRData();
    } catch (err) {
      toast.error('Failed to update leave status');
    }
  };

  const handleGenerateLetter = async (letterType, employeeId) => {
    try {
      const token = localStorage.getItem('crm_token');
      await axios.post(`${API}/hr/letters/generate`, null, {
        params: { 
          employee_id: employeeId, 
          letter_type: letterType 
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${letterType.replace('_', ' ')} letter generated!`);
      setShowLetterDialog(false);
      fetchHRData();
    } catch (err) {
      toast.error('Failed to generate letter');
    }
  };

  const handleGenerateIdCard = async (employeeId) => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.post(`${API}/hr/employees/id-card`, null, {
        params: { employee_id: employeeId },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('ID Card generated!');
    } catch (err) {
      toast.error('Failed to generate ID card');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': case 'present': case 'approved': case 'paid': case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'late': return 'bg-orange-100 text-orange-700';
      case 'absent': case 'rejected': case 'expired': return 'bg-red-100 text-red-700';
      case 'on_leave': case 'draft': return 'bg-blue-100 text-blue-700';
      case 'suspended': return 'bg-red-100 text-red-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = `${emp.firstName} ${emp.lastName} ${emp.email} ${emp.employeeId}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = filterDept === 'all' || emp.department === filterDept;
    return matchesSearch && matchesDept;
  });

  const handleStaffFormsUpload = async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const token = localStorage.getItem('crm_token');
    const fileArr = Array.from(selectedFiles);
    setStaffFormsProgress(fileArr.map(f => ({ name: f.name, done: false, error: false })));
    setStaffFormsUploading(true);
    try {
      const formData = new FormData();
      fileArr.forEach(f => formData.append('files', f));
      await axios.post(`${API}/hr/staff-forms/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setStaffFormsProgress(prev => prev.map((f, i) => pct >= ((i + 1) / fileArr.length) * 100 ? { ...f, done: true } : f));
          }
        }
      });
      setStaffFormsProgress(prev => prev.map(f => ({ ...f, done: true })));
      toast.success(`${fileArr.length} form(s) uploaded successfully!`);
      const formsRes = await axios.get(`${API}/hr/staff-forms`, { headers: { Authorization: `Bearer ${token}` } });
      setStaffForms(formsRes.data.forms || []);
    } catch {
      toast.error('Upload failed. Please try again.');
    } finally {
      setStaffFormsUploading(false);
      setTimeout(() => setStaffFormsProgress([]), 2500);
    }
  };

  const handleDeleteStaffForm = async (formId) => {
    if (!window.confirm('Delete this form template?')) return;
    const token = localStorage.getItem('crm_token');
    try {
      await axios.delete(`${API}/hr/staff-forms/${formId}`, { headers: { Authorization: `Bearer ${token}` } });
      setStaffForms(prev => prev.filter(f => f.id !== formId));
      toast.success('Form deleted.');
    } catch {
      toast.error('Failed to delete form.');
    }
  };

  const getFormIcon = (fileType) => {
    if (['pdf'].includes(fileType)) return { bg: 'bg-red-100', text: 'text-red-600', label: 'PDF' };
    if (['doc', 'docx'].includes(fileType)) return { bg: 'bg-blue-100', text: 'text-blue-600', label: 'WORD' };
    if (['xls', 'xlsx'].includes(fileType)) return { bg: 'bg-green-100', text: 'text-green-600', label: 'EXCEL' };
    return { bg: 'bg-stone-100', text: 'text-stone-600', label: (fileType || 'FILE').toUpperCase() };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 mx-auto mb-1 opacity-80" />
            <p className="text-2xl font-bold">{stats.totalEmployees}</p>
            <p className="text-xs text-blue-100">Total Employees</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <UserCheck className="w-5 h-5 mx-auto mb-1 opacity-80" />
            <p className="text-2xl font-bold">{stats.activeEmployees}</p>
            <p className="text-xs text-emerald-100">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-5 h-5 mx-auto mb-1 opacity-80" />
            <p className="text-2xl font-bold">{stats.presentToday}</p>
            <p className="text-xs text-teal-100">Present Today</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <Timer className="w-5 h-5 mx-auto mb-1 opacity-80" />
            <p className="text-2xl font-bold">{stats.lateToday}</p>
            <p className="text-xs text-orange-100">Late Today</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <Calendar className="w-5 h-5 mx-auto mb-1 opacity-80" />
            <p className="text-2xl font-bold">{stats.pendingLeaveRequests}</p>
            <p className="text-xs text-purple-100">Leave Requests</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-10 mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Overview</TabsTrigger>
          <TabsTrigger value="report-index" data-testid="tab-report-index" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">All Staff Index 1 &amp; 2</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports" className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white">HR Reports</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="letters" data-testid="tab-letters">Staff forms</TabsTrigger>
          <TabsTrigger value="sop" data-testid="tab-sop" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">SOP</TabsTrigger>
          <TabsTrigger value="circular" data-testid="tab-circular" className="data-[state=active]:bg-amber-700 data-[state=active]:text-white">Circular</TabsTrigger>
          <TabsTrigger value="ppt" data-testid="tab-ppt" className="data-[state=active]:bg-purple-900 data-[state=active]:text-white">Presentation PPT</TabsTrigger>
          <TabsTrigger value="project-stage" data-testid="tab-project-stage" className="data-[state=active]:bg-emerald-900 data-[state=active]:text-white">Project Work Stage Report</TabsTrigger>
          <TabsTrigger value="location-eod" data-testid="tab-location-eod" className="data-[state=active]:bg-violet-700 data-[state=active]:text-white">Location EOD</TabsTrigger>
        </TabsList>

        {/* Overview Tab — compiled % across all tabs incl. Index 1 & 2 */}
        <TabsContent value="overview">
          <HROverviewTab />
        </TabsContent>

        {/* Report Index Tab */}
        <TabsContent value="report-index">
          <ReportIndexTab user={user} isHR={true} startInAllView={true} />
        </TabsContent>

        {/* HR Reports — 15 sub-tabs */}
        <TabsContent value="reports">
          <HRReportsTab />
        </TabsContent>

        {/* SOP Tab */}
        <TabsContent value="sop">
          <SOPTab />
        </TabsContent>

        {/* Circular Tab */}
        <TabsContent value="circular">
          <CircularTab />
        </TabsContent>

        {/* Presentation PPT Tab */}
        <TabsContent value="ppt">
          <PresentationPPTTab />
        </TabsContent>

        {/* Project Work Stage Tab */}
        <TabsContent value="project-stage">
          <ProjectWorkStageTab />
        </TabsContent>

        {/* Location EOD Report — visible to Sahil (DBK-0014) and super_admin */}
        <TabsContent value="location-eod" data-testid="location-eod-content">
          <EODLocationReportTab user={user} />
        </TabsContent>

        {/* Overview Tab — REMOVED, but content kept in unused TabsContent for safety; never rendered */}
        <TabsContent value="overview-removed">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" className="h-16 flex-col gap-1" onClick={() => setActiveTab('employees')}>
                    <UserPlus className="w-5 h-5" />
                    <span className="text-xs">Add Employee</span>
                  </Button>
                  <Button variant="outline" className="h-16 flex-col gap-1" onClick={handleSignIn}>
                    <Clock className="w-5 h-5" />
                    <span className="text-xs">Sign In</span>
                  </Button>
                  <Button variant="outline" className="h-16 flex-col gap-1" onClick={handleSignOut}>
                    <Timer className="w-5 h-5" />
                    <span className="text-xs">Sign Out</span>
                  </Button>
                  <Button variant="outline" className="h-16 flex-col gap-1" onClick={() => setActiveTab('payroll')}>
                    <FileSpreadsheet className="w-5 h-5" />
                    <span className="text-xs">Payslips</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Pending Leave Requests */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  Pending Leave Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaveRequests.filter(l => l.status === 'pending').length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {leaveRequests.filter(l => l.status === 'pending').slice(0, 4).map((leave) => (
                      <div key={leave.id} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-sm">{leave.employeeName}</p>
                            <p className="text-xs text-stone-500">{leave.leaveType} - {leave.days} day(s)</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-1 h-8" onClick={() => handleLeaveAction(leave.id, 'approved')}>
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => handleLeaveAction(leave.id, 'rejected')}>
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-stone-400">
                    No pending leave requests
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Department Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Department Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {departmentDistribution.slice(0, 6).map((dept) => (
                    <div key={dept.department} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                      <span className="capitalize text-sm">{dept.department?.replace('_', ' ') || 'N/A'}</span>
                      <span className="font-bold text-sm">{dept.count}</span>
                    </div>
                  ))}
                  {departmentDistribution.length === 0 && (
                    <div className="text-center py-6 text-stone-400">
                      No department data
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Report Index Tab — DUPLICATE removed (moved to top) */}
        <TabsContent value="_report-index-old">
          <ReportIndexTab user={user} isHR={true} />
        </TabsContent>

        {/* Employees Tab — Excel-based attendance employees */}
        <TabsContent value="employees">
          <EmployeeManagementTab />
        </TabsContent>


        {/* Attendance Tab — contains Monthly Sheet + Leave Index sub-tabs */}
        <TabsContent value="attendance">
          <div className="space-y-0">
            {/* Sub-tabs */}
            <div className="flex border-b border-stone-200 mb-4 gap-1">
              {[
                { id: 'monthly', label: 'Monthly Attendance' },
                { id: 'leaveIndex', label: 'Leave Index' },
              ].map(t => (
                <button
                  key={t.id}
                  data-testid={`attendance-subtab-${t.id}`}
                  onClick={() => setAttendanceSubTab(t.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${attendanceSubTab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {attendanceSubTab === 'monthly' && <MonthlyAttendanceTab />}
            {attendanceSubTab === 'leaveIndex' && <LeaveIndexTab />}
          </div>
        </TabsContent>

        {/* Leave Tab */}
        <TabsContent value="leave">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Leave Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  Leave Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                    <span className="text-amber-700 text-sm">Pending</span>
                    <span className="font-bold text-amber-700">{leaveRequests.filter(l => l.status === 'pending').length}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                    <span className="text-emerald-700 text-sm">Approved</span>
                    <span className="font-bold text-emerald-700">{leaveRequests.filter(l => l.status === 'approved').length}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span className="text-red-700 text-sm">Rejected</span>
                    <span className="font-bold text-red-700">{leaveRequests.filter(l => l.status === 'rejected').length}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Leave Requests Table */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Leave Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Employee</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Duration</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Reason</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {leaveRequests.length > 0 ? leaveRequests.slice(0, 10).map((leave) => (
                        <tr key={leave.id} className="hover:bg-stone-50">
                          <td className="px-4 py-3">{leave.employeeName}</td>
                          <td className="px-4 py-3 capitalize">{leave.leaveType}</td>
                          <td className="px-4 py-3">{leave.days} day(s)</td>
                          <td className="px-4 py-3 max-w-xs truncate">{leave.reason}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(leave.status)}`}>
                              {leave.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {leave.status === 'pending' && (
                              <div className="flex gap-1">
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7" onClick={() => handleLeaveAction(leave.id, 'approved')}>
                                  Approve
                                </Button>
                                <Button size="sm" variant="outline" className="h-7" onClick={() => handleLeaveAction(leave.id, 'rejected')}>
                                  Reject
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="px-4 py-8 text-center text-stone-400">
                            No leave requests found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Payroll Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <IndianRupee className="w-5 h-5 text-emerald-600" />
                  Payroll Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button variant="outline" className="w-full justify-start">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Configure Salary
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Award className="w-4 h-4 mr-2" />
                    Add Incentive
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Payslips
                  </Button>
                  <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href='/dashboard/excel'}>
                    <Download className="w-4 h-4 mr-2" />
                    Salary Report
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Payslips Table */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-purple-600" />
                  Recent Payslips
                </CardTitle>
              </CardHeader>
              <CardContent>
                {payslips.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Employee</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Month</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-stone-600">Gross</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-stone-600">Deductions</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-stone-600">Net</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {payslips.map((payslip) => (
                          <tr key={payslip.id} className="hover:bg-stone-50">
                            <td className="px-4 py-3">{payslip.employeeName}</td>
                            <td className="px-4 py-3">{payslip.month}/{payslip.year}</td>
                            <td className="px-4 py-3 text-right">₹{payslip.grossEarnings?.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right text-red-600">₹{payslip.totalDeductions?.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-600">₹{payslip.netSalary?.toLocaleString()}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payslip.status)}`}>
                                {payslip.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-stone-400">
                    No payslips generated yet. Use "Generate Payslips" to create payslips for employees.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Staff Forms Tab */}
        <TabsContent value="letters">
          <Tabs defaultValue="library" className="space-y-4">
            <TabsList data-testid="staff-forms-subtabs" className="bg-stone-100">
              <TabsTrigger value="library" data-testid="staff-forms-library-tab" className="data-[state=active]:bg-white">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Form Templates Library
              </TabsTrigger>
              <TabsTrigger value="details" data-testid="staff-forms-details-tab-trigger" className="data-[state=active]:bg-white">
                <ClipboardList className="w-3.5 h-3.5 mr-1.5" /> Staff Forms Details
              </TabsTrigger>
              <TabsTrigger value="joinee" data-testid="staff-forms-joinee-tab-trigger" className="data-[state=active]:bg-white">
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> New Joinee
              </TabsTrigger>
            </TabsList>

            {/* ── Sub-tab 1: Form Templates Library (existing) ───── */}
            <TabsContent value="library">
          <div className="space-y-6">

            {/* ── Form Templates Library ──────────────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    Form Templates Library
                    {staffForms.length > 0 && (
                      <span className="ml-1 bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {staffForms.length} forms
                      </span>
                    )}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Drop zone */}
                <div
                  data-testid="staff-forms-dropzone"
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer mb-6
                    ${isDraggingForms ? 'border-emerald-500 bg-emerald-50' : 'border-stone-300 hover:border-emerald-400 hover:bg-stone-50'}`}
                  onDragOver={(e) => { e.preventDefault(); setIsDraggingForms(true); }}
                  onDragLeave={() => setIsDraggingForms(false)}
                  onDrop={(e) => { e.preventDefault(); setIsDraggingForms(false); handleStaffFormsUpload(e.dataTransfer.files); }}
                  onClick={() => document.getElementById('staff-forms-file-input').click()}
                >
                  <input
                    id="staff-forms-file-input"
                    data-testid="staff-forms-file-input"
                    type="file"
                    multiple
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg"
                    onChange={(e) => handleStaffFormsUpload(e.target.files)}
                  />
                  <Upload className="w-10 h-10 mx-auto mb-3 text-stone-400" />
                  <p className="font-semibold text-stone-700 mb-1">
                    {isDraggingForms ? 'Drop files here' : 'Drag & drop files or click to browse'}
                  </p>
                  <p className="text-sm text-stone-500">Upload all 25 forms at once — PDF, Word, Excel, etc. supported</p>
                  {staffFormsUploading && (
                    <div className="mt-4">
                      <div className="w-full bg-stone-200 rounded-full h-2 mb-3">
                        <div
                          className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${staffFormsProgress.filter(f => f.done).length / (staffFormsProgress.length || 1) * 100}%` }}
                        />
                      </div>
                      <p className="text-sm text-emerald-700 font-medium">
                        Uploading {staffFormsProgress.filter(f => f.done).length} / {staffFormsProgress.length} files...
                      </p>
                    </div>
                  )}
                  {!staffFormsUploading && staffFormsProgress.length > 0 && (
                    <p className="mt-3 text-sm text-emerald-600 font-semibold">✓ All {staffFormsProgress.length} files uploaded!</p>
                  )}
                </div>

                {/* Forms grid */}
                {staffForms.length === 0 ? (
                  <p className="text-center text-stone-400 py-8 text-sm">No form templates uploaded yet. Upload your forms above.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {staffForms.map((form) => {
                      const iconInfo = getFormIcon(form.file_type);
                      return (
                        <div
                          key={form.id}
                          data-testid={`staff-form-card-${form.id}`}
                          className="group relative border border-stone-200 rounded-xl p-3 hover:shadow-md transition-all bg-white flex flex-col gap-2"
                        >
                          <div className={`w-10 h-10 rounded-lg ${iconInfo.bg} flex items-center justify-center mx-auto`}>
                            <span className={`text-xs font-bold ${iconInfo.text}`}>{iconInfo.label}</span>
                          </div>
                          <p className="text-xs font-medium text-stone-700 text-center line-clamp-2 leading-tight" title={form.name}>
                            {form.name}
                          </p>
                          <p className="text-[10px] text-stone-400 text-center">{formatFileSize(form.file_size)}</p>
                          <div className="flex gap-1 mt-auto">
                            <a
                              href={`${API}/hr/staff-forms/${form.id}/download`}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`download-form-${form.id}`}
                              className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                            >
                              <Download className="w-3 h-3" /> Get
                            </a>
                            <button
                              data-testid={`delete-form-${form.id}`}
                              onClick={(e) => { e.stopPropagation(); handleDeleteStaffForm(form.id); }}
                              className="px-2 py-1 text-xs bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
            </TabsContent>

            {/* ── Sub-tab 2: Staff Forms Details (47-employee × 29-column matrix) ─── */}
            <TabsContent value="details">
              <StaffFormsDetailsTab />
            </TabsContent>

            {/* ── Sub-tab 3: New Joinee (add + upload 29 filled forms) ─── */}
            <TabsContent value="joinee">
              <NewJoineeTab
                onJoineeSaved={() => {
                  // refresh attendance employees list so the new joinee shows up
                  // in Staff Forms Details next time it's opened
                  fetchHRData();
                }}
              />
            </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="outsource">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Outsource Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-orange-600" />
                  Outsource Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <span className="text-orange-700 text-sm">Active Vendors</span>
                    <span className="font-bold text-orange-700">{outsourceVendors.filter(v => v.status === 'active').length}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-blue-700 text-sm">Total Workers</span>
                    <span className="font-bold text-blue-700">{outsourceWorkers.length}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <Button className="w-full bg-orange-600 hover:bg-orange-700" onClick={() => setShowVendorDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add Vendor
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Vendors Table */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-orange-600" />
                    Outsource Vendors
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {outsourceVendors.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Vendor</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Type</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Workers</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Contract End</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {outsourceVendors.map((vendor) => (
                          <tr key={vendor.id} className="hover:bg-stone-50">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium">{vendor.vendorName}</p>
                                <p className="text-xs text-stone-500">{vendor.contactPerson}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">{vendor.vendorType}</td>
                            <td className="px-4 py-3">{vendor.workerCount}</td>
                            <td className="px-4 py-3">{new Date(vendor.contractEndDate).toLocaleDateString()}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(vendor.status)}`}>
                                {vendor.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" className="h-7" onClick={() => { setSelectedVendor(vendor); setShowWorkerDialog(true); }}>
                                  Add Worker
                                </Button>
                                <Button size="sm" variant="outline" className="h-7">
                                  Attendance
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-stone-400">
                    No outsource vendors. Add vendors to manage outsourced teams.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Workers Table */}
            {outsourceWorkers.length > 0 && (
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Outsource Workers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Worker</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Vendor</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Type</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Location</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Shift</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-stone-600">Daily Rate</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {outsourceWorkers.map((worker) => (
                          <tr key={worker.id} className="hover:bg-stone-50">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium">{worker.workerName}</p>
                                <p className="text-xs text-stone-500">{worker.workerPhone || '-'}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">{worker.vendorName}</td>
                            <td className="px-4 py-3">{worker.workerType}</td>
                            <td className="px-4 py-3">{worker.assignedLocation || '-'}</td>
                            <td className="px-4 py-3">{worker.shift || '-'}</td>
                            <td className="px-4 py-3 text-right">₹{worker.dailyRate}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(worker.status)}`}>
                                {worker.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Reports Tab — DUPLICATE removed (moved to top) */}
        <TabsContent value="_reports-old">
          <HRReportsTab />
        </TabsContent>
      </Tabs>

      {/* Letter Generation Dialog */}
      <Dialog open={showLetterDialog} onOpenChange={setShowLetterDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate {selectedLetterType?.replace('_', ' ')} Letter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Select Employee</label>
              <Select onValueChange={(val) => setSelectedEmployee(employees.find(e => e.id === val))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.status === 'active').map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName} - {emp.department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700" 
              disabled={!selectedEmployee}
              onClick={() => handleGenerateLetter(selectedLetterType, selectedEmployee?.id)}
            >
              Generate Letter
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRDashboard;
